import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { UserMinusIcons } from '../../../../../../icons/icons';
import { NameEmailAvatar } from '../../../../../../ui';
import {
  IUserDisplay,
  PermissionType,
} from '../../../../../../utils/api_service/access_control';

export interface ISelectedUserPermissionsProps {
  title: string;
  users: IUserDisplay[] | undefined;
  onPermissionChange?: (userId: string, permissions: PermissionType[]) => void;
  readOnly?: boolean;
  onDelete?: (user: IUserDisplay) => void;
}

export const SelectedUserPermissions: React.FC<ISelectedUserPermissionsProps> = ({
  title,
  users,
  onPermissionChange,
  readOnly = false,
  onDelete,
}) => {
  const [permissions, setPermissions] = useState<
    Record<string, PermissionType[]>
  >({});

  useEffect(() => {
    if (users) {
      const newPerms: Record<string, PermissionType[]> = {};
      users.forEach((user) => {
        newPerms[user.id] = user.permissions || [];
      });
      setPermissions(newPerms);
    }
  }, [users]);

  const handleCheckbox = (userId: string, permType: PermissionType) => {
    setPermissions((prev) => {
      const prevPerms = prev[userId] || [];
      let updated: PermissionType[];
      if (prevPerms.includes(permType)) {
        updated = prevPerms.filter((p) => p !== permType);
      } else {
        updated = [...prevPerms, permType];
      }
      onPermissionChange?.(userId, updated);
      return { ...prev, [userId]: updated };
    });
  };

  const permissionTypes: PermissionType[] = ['read', 'write', 'delete'];

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, color: 'text.primary', fontSize: { xs: '1rem', sm: '1.125rem' } }}>
        {title}
      </Typography>

      {/* Mobile Card List */}
      <Stack spacing={1} sx={{ display: { xs: 'flex', sm: 'none' } }}>
        {users?.map((user) => (
          <Paper
            key={user.id || user.email}
            variant="outlined"
            sx={{ p: 1.5, borderRadius: 2, bgcolor: 'grey.50' }}
          >
            <Box sx={{ mb: 1.5 }}>
              <NameEmailAvatar
                name={`${user.firstName} ${user.lastName}`}
                email={user.email}
                avatar=""
              />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
              {permissionTypes.map((permType) => (
                <Paper
                  key={permType}
                  variant="outlined"
                  sx={{
                    px: 1,
                    py: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderRadius: 1.5,
                    bgcolor: 'background.paper',
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'capitalize', color: 'text.secondary' }}>
                    {permType}
                  </Typography>
                  <Checkbox
                    size="small"
                    color="success"
                    checked={permissions[user.id]?.includes(permType) ?? false}
                    onChange={() => handleCheckbox(user.id, permType)}
                    disabled={readOnly}
                    sx={{ p: 0.25 }}
                  />
                </Paper>
              ))}
            </Box>
            {readOnly && onDelete && (
              <Button
                fullWidth
                variant="outlined"
                color="error"
                size="small"
                onClick={() => onDelete(user)}
                sx={{ mt: 1.5, fontWeight: 600, textTransform: 'none' }}
              >
                Remove Access
              </Button>
            )}
          </Paper>
        ))}
      </Stack>

      {/* Desktop Table View */}
      <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: 'grey.50' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 120 }}>Read</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 120 }}>Write</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 120 }}>Delete</TableCell>
                {readOnly && <TableCell sx={{ width: 60 }} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {users?.map((user, i) => (
                <TableRow
                  key={user.id || user.email}
                  sx={{ bgcolor: i % 2 === 0 ? 'background.paper' : 'grey.50' }}
                >
                  <TableCell>
                    <NameEmailAvatar
                      name={`${user.firstName} ${user.lastName}`}
                      email={user.email}
                      avatar=""
                    />
                  </TableCell>
                  {permissionTypes.map((permType) => (
                    <TableCell key={permType}>
                      <Checkbox
                        size="small"
                        color="success"
                        checked={permissions[user.id]?.includes(permType) ?? false}
                        onChange={() => handleCheckbox(user.id, permType)}
                        disabled={readOnly}
                        sx={{ p: 0.5 }}
                      />
                    </TableCell>
                  ))}
                  {readOnly && onDelete && (
                    <TableCell align="center">
                      <Tooltip title="Delete Permission">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => onDelete(user)}
                        >
                          <UserMinusIcons size={20} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};
